import React, {useMemo} from 'react'
import {
    Box,
    Text,
    Link as ChakraLink,

    /* Hooks */
    useMultiStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'

/* Utils */
import { generateStyles } from '../../utils/utils'
import { renderComponentsSwitch } from '../../utils/contentRenderer'

/* Context */
import { useGetLinkData } from '../../../../contexts/category-url-context'
import { Link as RouterLink } from 'react-router-dom'

const WidgetTileBlock = ({
    ResponsiveMedia,
    Title,
    WidgetTileBlockTitleStyle,
    SubTitle,
    SubTitleStyle,
    TileCTAs,
    TextBellowMedia,
    overwriteWidgetTitleStyle,
    overwriteWidgetSubTitleStyle,
    overwriteWidgetTileCtaWrapperStyle,
    widgetTileBlockImageOverlay = false,
    overwriteWidgetTileBlockTextCtaStyle,
    customMediaContainerStyling,
    // Image optimization props (passed from parent grid)
    widths = null,
    sizes = null,
    preset = 'TILE',
    cdnOptions = null,
    ...props
}) => {
    const global = useMultiStyleConfig('GlobalCMS')
    const styles = useMultiStyleConfig('WidgetTileBlock', {
        TextBellowMedia: TextBellowMedia,
        ...props
    })
    const ctaItemBlockStyles = useMultiStyleConfig('CtaItemBlock', {
        position: 'absolute',
        Alignment: TileCTAs?.length > 0 && TileCTAs[0].ContentLink?.Expanded?.Alignment
    })

    const mediaLink = ResponsiveMedia?.length > 0 && ResponsiveMedia[0]?.ContentLink?.Expanded?.Link
    let linkData = null

    if (mediaLink) {
        const getLinkData = useGetLinkData()
        linkData = useMemo(() => {
            return getLinkData(mediaLink?.Expanded)
        }, [mediaLink?.Expanded])
    }

    const isExternalUrl = linkData?.isExternalUrl || false
    const hasLink = mediaLink && linkData?.generatedURL
    const linkComponent = isExternalUrl ? ChakraLink : RouterLink

    return (
        <Box as={hasLink ? linkComponent : Box}
            {...(hasLink && isExternalUrl ? { href: linkData?.generatedURL } : {})}
            {...(hasLink && !isExternalUrl ? { to: linkData?.generatedURL } : {})}
            {...(hasLink && !linkData?.stayOnSamePage && {
                target: linkData?.target,
                rel: linkData?.rel
            })}
            data-block-type="WidgetTileBlock">
            <Box {...styles.container} >
                {widgetTileBlockImageOverlay &&
                    <Box {...global.overlay}></Box>
                }
                {ResponsiveMedia?.map((rm, index) => {
                    return renderComponentsSwitch(rm?.ContentLink?.Expanded, {
                        key: `WidgetTileBlock-${rm?.ContentLink?.id}-${index}`,
                        widths,
                        sizes,
                        preset,
                        cdnOptions,
                        asLink: false
                    })
                })}
                <Box {...ctaItemBlockStyles.container}>
                    {SubTitle && !TextBellowMedia &&
                        <Text {...generateStyles(SubTitleStyle, { ...styles.SubTitleStyle, ...overwriteWidgetSubTitleStyle })}>
                            {SubTitle}
                        </Text>
                    }
                    {Title && !TextBellowMedia &&
                        <Text {...generateStyles(WidgetTileBlockTitleStyle, { ...styles.WidgetTileBlockTitleStyle, ...overwriteWidgetTitleStyle }, { h2: 'sm' })}>
                            {Title}
                        </Text>
                    }

                    <Box {...styles.tileCtasWrapper} {...overwriteWidgetTileCtaWrapperStyle}>
                        {TileCTAs?.map((TileCTA, index) => {
                            const ctaProps = {
                                key: `CtaItemBlock-${TileCTA?.ContentLink?.id}-${index}`,
                                position: 'static',
                                stylingTextOverwrite: {
                                    ...overwriteWidgetTileBlockTextCtaStyle
                                },
                                customMediaContainerStyling: {...customMediaContainerStyling}
                            }
                            return renderComponentsSwitch(TileCTA?.ContentLink?.Expanded, { ...ctaProps })
                        })}
                    </Box>
                </Box>
            </Box>

            {SubTitle && TextBellowMedia &&
                <Text {...generateStyles(SubTitleStyle, { ...styles.SubTitleStyle, ...overwriteWidgetSubTitleStyle })}>
                    {SubTitle}
                </Text>
            }
            {Title && TextBellowMedia &&
                <Text {...generateStyles(WidgetTileBlockTitleStyle, { ...styles.WidgetTileBlockTitleStyle, ...overwriteWidgetTitleStyle }, { h2: 'sm' })}>
                    {Title}
                </Text>
            }
        </Box>
    )
}

WidgetTileBlock.propTypes = {
    ResponsiveMedia: PropTypes.array,
    Title: PropTypes.string,
    WidgetTileBlockTitleStyle: PropTypes.object,
    SubTitle: PropTypes.string,
    SubTitleStyle: PropTypes.object,
    TileCTAs: PropTypes.array,
    TextBellowMedia: PropTypes.bool,
    overwriteWidgetTitleStyle: PropTypes.object,
    overwriteWidgetSubTitleStyle: PropTypes.object,
    overwriteWidgetTileCtaWrapperStyle: PropTypes.object,
    widgetTileBlockImageOverlay: PropTypes.bool,
    widths: PropTypes.arrayOf(PropTypes.number),
    sizes: PropTypes.string,
    preset: PropTypes.string,
    cdnOptions: PropTypes.object
}

export default WidgetTileBlock
